<template>
  <LoadingContainer :is-loading="isLoading">
    <form novalidate @submit.prevent="submit">
      <div class="card">
        <div class="card-header">
          <div class="card-header-title">
            <div class="title is-6 is-marginless">
              Προιόντα
            </div>
          </div>
          <div class="card-header-icon">
            <div class="field has-addons is-marginless">
              <div class="control">
                <router-link :to="{ name: 'products.list' }" class="button">
                  <span class="icon is-small"><i class="fa fa-list-ul"/></span
                  ><span>Λίστα</span>
                </router-link>
              </div>
            </div>
          </div>
        </div>

        <div class="card-content">
          <div class="columns is-multiline">
            <div class="column is-full">
              <div class="field">
                <label class="label">Ονομασία *</label>
                <div class="control">
                  <input
                    v-validate="'required'"
                    v-model.trim="general.title"
                    class="input"
                    type="text"
                    name="title"
                  />
                  <p v-show="errors.has('title')" class="help is-danger">
                    Εισάγετε ονομασία
                  </p>
                </div>
              </div>
            </div>

            <div class="column is-full">
              <div class="field">
                <label class="label">Περιγραφή *</label>
                <div class="control">
                  <textarea
                    v-validate="'required'"
                    v-model.trim="general.description"
                    class="textarea"
                    name="description"
                  />
                  <p v-show="errors.has('description')" class="help is-danger">
                    Εισάγετε περιγραφή
                  </p>
                </div>
              </div>
            </div>

            <div class="column is-full">
              <div class="field">
                <label class="label">Περιεχόμενο *</label>
                <div class="control">
                  <VueEditor
                    v-validate="'required'"
                    id="editor-1"
                    v-model="general.content"
                    :editor-toolbar="toolbarOptions"
                    data-vv-value-path="value"
                    data-vv-name="content"
                    @input="changeContent('content', $event)"
                  />
                </div>
                <p v-show="errors.has('content')" class="help is-danger">
                  Εισάγετε περιεχόμενο
                </p>
              </div>
            </div>

            <div class="column is-full">
              <div class="field">
                <label class="label">Συστατικά</label>
                <div class="control">
                  <VueEditor
                    id="editor-2"
                    :editor-toolbar="toolbarOptions"
                    v-model="general.ingredients"
                    @input="changeContent('ingredients', $event)"
                  />
                </div>
              </div>
            </div>
          </div>

          <div class="columns is-multiline">
            <div class="column is-half">
              <div class="field">
                <label class="label">Διαθεσιμότητα *</label>
                <div class="control">
                  <div class="select is-fullwidth">
                    <select
                      v-model.trim.number="general.availability_id"
                      name="availability_id"
                    >
                      <option
                        v-for="item in availabilities"
                        :key="item.id"
                        :value="item.id"
                      >
                        {{ item.title }}
                      </option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div class="column is-half">
              <div class="field">
                <label class="label">Βάρος *</label>
                <div class="control">
                  <input
                    v-validate="'required|excluded:0'"
                    v-model.trim.number="general.weight"
                    class="input"
                    type="number"
                    name="weight"
                    min="0"
                    step="0.01"
                  />
                  <p v-show="errors.has('weight')" class="help is-danger">
                    Εισάγετε βάρος
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ToggleableCard :is-open="true" title="Group Items">
        <div class="card-content">
          <table
            v-if="general.subgroups.length"
            class="table is-bordered is-striped is-fullwidth"
          >
            <thead>
              <tr>
                <th>#</th>
                <th>Φώτο</th>
                <th>Barcodes</th>
                <th>Common ID</th>
                <th>Τίτλος</th>
                <th>Π.Λ. Τιμή (&euro;)</th>
                <th>Έκπτωση (&#x00025;)</th>
                <th>Τελική Τιμή (&euro;)</th>
                <th>Προβολή</th>
                <th>Ενέργειες</th>
              </tr>
            </thead>
            <tbody>
              <subgroup
                v-for="(item, index) in general.subgroups"
                :item="item"
                :index="index"
                :key="item.id"
                @onDelete="onDeleteSubgroup"
              />
            </tbody>
          </table>

          <subgroups-search
            :group="general"
            @selectProduct="onSelectSubgroup"
          />
        </div>
      </ToggleableCard>

      <ToggleableCard :is-open="true" title="Τιμολογιακή Πολιτική">
        <div class="card-content">
          <EditPrice :product="general" type="group" @updatePrice="updatePrice">
            <div class="column is-half">
              <div class="field">
                <label class="label">ΦΠΑ *</label>
                <div class="control">
                  <div class="select is-fullwidth">
                    <select v-model.trim.number="general.tax_id" name="tax_id">
                      <option
                        v-for="item in taxes"
                        :key="item.id"
                        :value="item.id"
                      >
                        {{ item.percentage }} %
                      </option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </EditPrice>
        </div>
      </ToggleableCard>

      <ToggleableCard :is-open="true" title="Κατηγορίες">
        <div class="card-content">
          <EditCategories
            :categories="general.categories"
            @updateCategories="updateCategories"
          />
        </div>
      </ToggleableCard>

      <ToggleableCard :is-open="true" title="Brand & Collection">
        <div class="card-content">
          <div class="columns is-multiline">
            <div class="column is-full">
              <label class="label">Brand *</label>
              <brands-search
                :brand="general.brand"
                @selectBrand="selectBrand"
                @removeBrand="removeBrand"
              />
              <p v-show="errors.has('brand')" class="help is-danger">
                Επιλέξτε brand
              </p>
            </div>

            <div v-if="general.brand_id" class="column is-full">
              <label class="label">Collection</label>
              <collections-brands-search
                :brand="general.brand"
                :collection="general.collection"
                @selectCollection="selectCollection"
                @removeCollection="removeCollection"
              />
            </div>
          </div>
        </div>
      </ToggleableCard>

      <ToggleableCard :is-open="true" title="Συμπτώματα & Tags">
        <div class="card-content">
          <div class="columns is-multiline">
            <div class="column is-full">
              <label class="label">Συμπτώματα</label>
              <multiselect
                v-model="general.symptoms"
                :options="symptoms"
                :multiple="true"
                name="symptoms"
                class="multiple"
                track-by="id"
                label="title"
                placeholder="Επιλέξτε symptoms"
                selected-label="Επιλεγμένο"
                select-label="Πατήστε enter για επιλογή"
                deselect-label="Πατήστε enter για απο-επιλογή"
              />
            </div>

            <div class="column is-full">
              <div class="field">
                <label class="label">Tags</label>
                <div class="control">
                  <multiselect
                    v-model="general.tags"
                    :options="tags"
                    :multiple="true"
                    name="tags"
                    class="multiple"
                    track-by="id"
                    label="title"
                    placeholder="Επιλέξτε tags"
                    selected-label="Επιλεγμένο"
                    select-label="Πατήστε enter για επιλογή"
                    deselect-label="Πατήστε enter για απο-επιλογή"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </ToggleableCard>

      <ToggleableCard :is-open="true" title="Φίλτρα">
        <div class="card-content">
          <div class="columns is-multiline">
            <div class="column is-full">
              <div class="field">
                <label class="label">FilterGroups</label>
                <div class="control">
                  <multiselect
                    :value="general.filterGroups"
                    :options="filterGroups"
                    :multiple="true"
                    name="filterGroups"
                    class="multiple"
                    track-by="id"
                    label="title"
                    placeholder="Επιλέξτε filterGroups"
                    selected-label="Επιλεγμένο"
                    select-label="Πατήστε enter για επιλογή"
                    deselect-label="Πατήστε enter για απο-επιλογή"
                    @select="selectFilterGroup"
                    @remove="removeFilterGroup"
                  />
                </div>
              </div>
            </div>
            <div
              v-for="(filterGroup, index) in general.filterGroups"
              :key="filterGroup.id"
              class="column is-full"
            >
              <div class="field">
                <label class="label">
                  FilterGroup: {{ filterGroup.title }}
                </label>
                <div class="control">
                  <multiselect
                    :value="filterGroup.selections"
                    :options="filterGroup.filters"
                    :multiple="true"
                    :name="`filterGroups[${index}].filters`"
                    class="multiple"
                    track-by="id"
                    label="title"
                    placeholder="Επιλέξτε filters"
                    selected-label="Επιλεγμένο"
                    select-label="Πατήστε enter για επιλογή"
                    deselect-label="Πατήστε enter για απο-επιλογή"
                    @select="selectFilter"
                    @remove="removeFilter"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </ToggleableCard>

      <div class="card">
        <div class="card-footer">
          <FormSubmit :is-saving="isSaving" @reset="reset" />
        </div>
      </div>
    </form>
  </LoadingContainer>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import Multiselect from 'vue-multiselect';
import { VueEditor } from 'vue2-editor';
import _flow from 'lodash/flow';
import _get from 'lodash/get';
import _sortBy from 'lodash/sortBy';
import _uniq from 'lodash/uniq';
import AddItem from '@/views/components/AddItem';
import ToggleableCard from '@/views/components/ToggleableCard';
import LoadingContainer from '@/views/components/LoadingContainer';
import toolbarOptions from '@/constants/toolbarOptions';
import EditPrice from '@/views/components/EditPrice';
import EditCategories from '../components/EditCategories';
import BrandsSearch from '../components/BrandsSearch';
import CollectionsBrandsSearch from '../components/CollectionsBrandsSearch';
import SymptomsSearch from '../components/SymptomsSearch';
import SubgroupsSearch from './components/SubgroupsSearch';
import Subgroup from './components/Subgroup';

export default {
  components: {
    VueEditor,
    Multiselect,
    LoadingContainer,
    ToggleableCard,
    EditCategories,
    EditPrice,
    BrandsSearch,
    CollectionsBrandsSearch,
    SymptomsSearch,
    SubgroupsSearch,
    Subgroup,
  },
  extends: AddItem,
  data() {
    return {
      general: {
        title: '',
        slug: '',
        description: '',
        content: '',
        ingredients: '',
        buy_price: 0,
        sell_price: 0,
        discount: 0,
        final_price: 0,
        weight: 0,
        tax_id: null,
        availability_id: 1,
        tags: [],
        brand_id: null,
        brand: null,
        collection_id: null,
        collection: null,
        symptoms: [],
        categories: [],
        subgroups: [],
        filterGroups: [],
      },
      isSaving: false,
      toolbarOptions,
    };
  },
  computed: {
    ...mapGetters({
      availabilities: 'getAvailabilities',
      taxes: 'taxes/list/getTaxes',
      tags: 'tags/all/getTags',
      symptoms: 'symptoms/all/getSymptoms',
      filterGroups: 'filterGroups/all/getFilterGroups',
      categoriesReverseTree: 'categories/list/getCategoriesReverseTree',
      isLoading: 'categories/list/getIsLoading',
    }),
  },
  watch: {
    taxes(newVal) {
      this.general.tax_id = newVal.find(tax => tax.default === 1).id;
    },
  },
  async created() {
    try {
      await Promise.all([
        this.getTags(),
        this.getTaxes(),
        this.getCategories(),
        this.getSymptoms(),
        this.getFilterGroups(),
      ]);
    } catch (err) {
      this.$router.push('/error');
    }
  },
  methods: {
    ...mapActions({
      getTags: 'tags/all/getTags',
      getTaxes: 'taxes/list/getTaxes',
      getCategories: 'categories/list/getCategories',
      getSymptoms: 'symptoms/all/getSymptoms',
      addGroup: 'groups/add/addGroup',
      getFilterGroups: 'filterGroups/all/getFilterGroups',
    }),
    async submit() {
      try {
        const isValid = await this.$validator.validateAll();

        if (!isValid) {
          throw Error('Client-side validation failed');
        }

        this.isSaving = true;
        await this.addGroup({
          ...this.general,
          tags: this.general.tags.map(({ id }) => id),
          symptoms: this.general.symptoms.map(({ id }) => id),
          categories: this.general.categories.map(
            ({ uuid }) => this.categoriesReverseTree[uuid].id,
          ),
          subgroups: this.general.subgroups.map(({ id }) => id),
          filters: this.general.filterGroups.reduce((acc, filterGroup) => {
            const ids = _get(filterGroup, 'selections', []).map(({ id }) => id);

            return _flow(_uniq, _sortBy)([...acc, ...ids]);
          }, []),
        });
        this.isSaving = false;

        this.$notify({
          type: 'success',
          title: 'Επιτυχία',
          text: 'Το προιόν αποθηκεύτηκε',
        });
      } catch (err) {
        this.isSaving = false;
        this.$notify({
          type: 'error',
          title: 'Αποτυχία',
          text: 'To αίτημα απέτυχε',
        });
      }
    },
    reset() {
      this.general = {
        title: '',
        slug: '',
        description: '',
        content: '',
        ingredients: '',
        buy_price: 0,
        sell_price: 0,
        discount: 0,
        final_price: 0,
        weight: 0,
        tax_id: this.defaultTax,
        availability_id: 1,
        tags: [],
        brand_id: null,
        brand: null,
        collection_id: null,
        collection: null,
        symptoms: [],
        categories: [],
        subgroups: [],
        filterGroups: [],
      };

      this.$validator.reset();
    },
    changeContent(el, content) {
      this.general[el] = content === '<p><br></p>' ? '' : content;
    },
    selectBrand(brand) {
      this.general = {
        ...this.general,
        brand_id: brand.id,
        brand,
        collection_id: null,
        collection: null,
      };
    },
    removeBrand() {
      this.general = {
        ...this.general,
        brand_id: null,
        brand: null,
        collection_id: null,
        collection: null,
      };
    },
    selectCollection(collection) {
      this.general = {
        ...this.general,
        collection_id: collection.id,
        collection,
      };
    },
    removeCollection() {
      this.general = {
        ...this.general,
        collection_id: null,
        collection: null,
      };
    },
    updateCategories({ categories }) {
      this.general = {
        ...this.general,
        categories,
      };
    },
    updatePrice(price) {
      this.general = {
        ...this.general,
        ...price,
      };
    },
    onDeleteSubgroup(subgroup) {
      this.general = {
        ...this.general,
        subgroups: this.general.subgroups.filter(
          ({ id }) => id !== subgroup.id,
        ),
      };
    },
    onSelectSubgroup(subgroup) {
      this.general = {
        ...this.general,
        subgroups: [
          ...this.general.subgroups,
          {
            ...subgroup,
          },
        ],
      };
    },
    selectFilterGroup(filterGroup) {
      this.general = {
        ...this.general,
        filterGroups: [
          ...this.general.filterGroups,
          {
            ...filterGroup,
            selections: [],
          },
        ],
      };
    },
    removeFilterGroup(filterGroup) {
      this.general = {
        ...this.general,
        filterGroups: this.general.filterGroups.filter(
          ({ id }) => id !== filterGroup.id,
        ),
      };
    },
    selectFilter(filter) {
      this.general = {
        ...this.general,
        filterGroups: this.general.filterGroups.map(filterGroup =>
          filter.filter_group_id === filterGroup.id
            ? {
                ...filterGroup,
                selections: [...filterGroup.selections, filter],
              }
            : filterGroup,
        ),
      };
    },
    removeFilter(filter) {
      this.general = {
        ...this.general,
        filterGroups: this.general.filterGroups.map(filterGroup =>
          filter.filter_group_id === filterGroup.id
            ? {
                ...filterGroup,
                selections: filterGroup.selections.filter(
                  ({ id }) => id !== filter.id,
                ),
              }
            : filterGroup,
        ),
      };
    },
  },
};
</script>

<style scoped>
.multiselect {
  margin: 0 auto !important;
}
</style>
